/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Transcriptions API
 * API for managing audio transcription sessions and transcripts. All operations are scoped to the authenticated user\'s organization. 
 *
 * The version of the OpenAPI document: 1.0.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * Precomputed AI summary covering all transcripts of one appointment
 */
export interface AppointmentTranscriptSummaryResponse {
    /**
     * The generated Dutch summary text
     */
    'summary': string;
    /**
     * When the summary was generated (epoch milliseconds)
     */
    'generatedAt': number;
    /**
     * How many completed transcripts the summary covers
     */
    'transcriptCount': number;
}
/**
 * Request to start a transcription session. Exactly one of `appointmentId` or `formSubmissionId` must be supplied — the session is owned by that entity. 
 */
export interface CreateTranscriptionSessionRequest {
    /**
     * Appointment to own the session (mutually exclusive with formSubmissionId)
     */
    'appointmentId'?: string | null;
    /**
     * Form submission to own the session (mutually exclusive with appointmentId)
     */
    'formSubmissionId'?: string | null;
    /**
     * Language code (e.g. nl, en). Defaults to nl.
     */
    'language'?: string | null;
}
/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * Recording session status: - ACTIVE: Currently recording - STOPPED: Recording stopped, awaiting processing - PROCESSING: Audio being transcribed - COMPLETED: Transcription finished - FAILED: Processing failed 
 */

export const SessionStatus = {
    Active: 'ACTIVE',
    Stopped: 'STOPPED',
    Processing: 'PROCESSING',
    Completed: 'COMPLETED',
    Failed: 'FAILED'
} as const;

export type SessionStatus = typeof SessionStatus[keyof typeof SessionStatus];


/**
 * The role of a transcript speaker, derived from the source of their voice (#4639)
 */

export const SpeakerRole = {
    Behandelaar: 'BEHANDELAAR',
    Client: 'CLIENT'
} as const;

export type SpeakerRole = typeof SpeakerRole[keyof typeof SpeakerRole];


/**
 * A segment from real-time streaming transcription. Uses generic speaker hints (A, B) instead of full diarization. 
 */
export interface StreamingSegmentDto {
    /**
     * Sequence number for ordering
     */
    'sequence': number;
    /**
     * Transcribed text for this segment
     */
    'text': string;
    /**
     * Whether this was a final (stable) or interim result
     */
    'isFinal': boolean;
    /**
     * Generic speaker label from VAD (A, B, etc.)
     */
    'speakerHint'?: string | null;
    /**
     * Start time in milliseconds (if available)
     */
    'startMs'?: number | null;
    /**
     * End time in milliseconds (if available)
     */
    'endMs'?: number | null;
}
/**
 * Response containing streaming transcription segments. These segments were captured during live recording using whisper_streaming and serve as a fallback when batch transcription fails. 
 */
export interface StreamingSegmentsResponse {
    /**
     * Appointment this transcript belongs to
     */
    'appointmentId': string;
    /**
     * Streaming segments ordered by sequence number
     */
    'segments': Array<StreamingSegmentDto>;
    /**
     * Whether streaming data is available for this transcript
     */
    'hasStreamingData': boolean;
}
export interface TranscriptListResponse {
    /**
     * List of transcript summaries, ordered by createdAt descending
     */
    'transcripts': Array<TranscriptSummary>;
}
export interface TranscriptResponse {
    /**
     * Unique transcript identifier
     */
    'id': string;
    /**
     * Appointment this transcript belongs to (null for standalone form transcriptions)
     */
    'appointmentId'?: string | null;
    /**
     * Form submission this transcript belongs to (null for appointment transcriptions)
     */
    'formSubmissionId'?: string | null;
    /**
     * The transcribed text (null while processing)
     */
    'transcriptText'?: string | null;
    'status': TranscriptionStatus;
    /**
     * Language of the transcript
     */
    'language'?: string | null;
    /**
     * Timestamp when transcription was created/started (epoch milliseconds)
     */
    'transcribedAt': number;
    /**
     * Timestamp when transcript was soft deleted (epoch milliseconds). Only relevant for admin views.
     */
    'deletedAt'?: number | null;
    /**
     * Why the transcription failed, as published by the whisper service. Null unless status is FAILED. See TranscriptSummary.errorCode for the recognised values. 
     */
    'errorCode'?: string | null;
    /**
     * Speaker-labeled transcript segments, ordered by sequence
     */
    'segments'?: Array<TranscriptSegmentDto>;
}


export interface TranscriptSegmentDto {
    /**
     * Unique segment identifier
     */
    'id': string;
    /**
     * Speaker identifier (1-4)
     */
    'speakerLabel': number;
    /**
     * Who this speaker is, when the recording can tell: in an online recording the microphone is the behandelaar and the call is the cliënt (#4639). Null for a recording that was not online, and for a speaker whose voice could not be attributed to one source — shown as \"Spreker N\". 
     */
    'speakerRole'?: SpeakerRole | null;
    /**
     * Transcribed text for this segment
     */
    'text': string;
    /**
     * Start time of segment in milliseconds
     */
    'startTimeMs'?: number | null;
    /**
     * End time of segment in milliseconds
     */
    'endTimeMs'?: number | null;
}


/**
 * Summary of a transcript without the full text or segments
 */
export interface TranscriptSummary {
    /**
     * Unique transcript identifier
     */
    'id': string;
    'status': TranscriptionStatus;
    /**
     * Timestamp when transcription was created (epoch milliseconds)
     */
    'createdAt': number;
    /**
     * Duration of the audio in milliseconds (derived from segments)
     */
    'durationMs'?: number | null;
    /**
     * Why the transcription failed, as published by the whisper service. Null unless status is FAILED, and null for a failure published before this field existed.  Not a closed set: `NoAudioCaptured` (the recording captured no audio) and `RecordingInterrupted` (the recording was cut short and could not be saved) are the two a client should recognise; anything else is the exception class name of a batch processing failure and should render as a generic reason. 
     */
    'errorCode'?: string | null;
}


export interface TranscriptionSessionListResponse {
    /**
     * List of transcription sessions
     */
    'sessions': Array<TranscriptionSessionResponse>;
}
export interface TranscriptionSessionResponse {
    /**
     * Session identifier for streaming audio chunks
     */
    'sessionId': string;
    /**
     * Appointment this session belongs to (null if owned by form submission)
     */
    'appointmentId'?: string | null;
    /**
     * Form submission this session belongs to (null if owned by appointment)
     */
    'formSubmissionId'?: string | null;
    'status': SessionStatus;
    /**
     * Session start timestamp (epoch milliseconds)
     */
    'startedAt': number;
    /**
     * Number of audio chunks uploaded to this session
     */
    'chunksUploaded'?: number;
    /**
     * Total bytes received across all chunks
     */
    'totalBytesReceived'?: number;
    /**
     * Timestamp of the last uploaded chunk (epoch milliseconds)
     */
    'lastChunkAt'?: number | null;
}


/**
 * Transcription processing status: - PENDING: Waiting to be processed - PROCESSING: Currently being transcribed - COMPLETED: Transcription finished successfully - FAILED: Transcription failed 
 */

export const TranscriptionStatus = {
    Pending: 'PENDING',
    Processing: 'PROCESSING',
    Completed: 'COMPLETED',
    Failed: 'FAILED'
} as const;

export type TranscriptionStatus = typeof TranscriptionStatus[keyof typeof TranscriptionStatus];



/**
 * TranscriptionsApi - axios parameter creator
 */
export const TranscriptionsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Starts a transcription session owned by exactly one of an appointment or a form submission. The request body must supply exactly one of `appointmentId` or `formSubmissionId`; a request with neither or both is rejected with 400.  When `formSubmissionId` is supplied and that submission is linked to an appointment, the session is owned by the appointment instead (matching the form-submission ownership rules).  An ACTIVE session the owner still carries is superseded — marked FAILED — rather than rejected. A session only leaves ACTIVE on a clean stop, so a recording killed mid-stream would otherwise make its appointment permanently unrecordable (#3350). 
         * @summary Start a transcription session
         * @param {CreateTranscriptionSessionRequest} createTranscriptionSessionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createTranscriptionSession: async (createTranscriptionSessionRequest: CreateTranscriptionSessionRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createTranscriptionSessionRequest' is not null or undefined
            assertParamExists('createTranscriptionSession', 'createTranscriptionSessionRequest', createTranscriptionSessionRequest)
            const localVarPath = `/api/v1/transcription-sessions`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createTranscriptionSessionRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Soft-deletes a specific transcript, loaded by `(transcriptId, organizationId)` directly. Requires transcripts.delete or recordings.delete permission.  Refused with `409` while the transcript\'s transcription session is still `ACTIVE`. A delete only sets `deleted_at` and says nothing to the whisper service, so deleting a transcript that is still being recorded into leaves that recording finalizing into a soft-deleted row. 
         * @summary Delete transcript by ID
         * @param {string} transcriptId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteTranscriptById: async (transcriptId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'transcriptId' is not null or undefined
            assertParamExists('deleteTranscriptById', 'transcriptId', transcriptId)
            const localVarPath = `/api/v1/transcripts/{transcriptId}`
                .replace(`{${"transcriptId"}}`, encodeURIComponent(String(transcriptId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns one AI-generated summary covering **all** transcripts of the appointment, grouped together.  The summary is precomputed and stored server-side whenever the appointment\'s transcripts change, so this normally reads a stored row rather than calling the model. On a miss (an appointment transcribed before the summary was introduced, or a failed warm-up) it is generated and stored on the spot.  Returns 204 No Content when the appointment has no completed transcript text, or when the model judged the content too thin for a meaningful summary.  Callers holding only `recordings.read.own` must own the appointment (behandelaar/attendee). 
         * @summary Get the AI summary of an appointment\'s transcripts
         * @param {string} appointmentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentTranscriptSummary: async (appointmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('getAppointmentTranscriptSummary', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/appointments/{appointmentId}/transcript-summary`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a specific transcript with its segments, loaded by `(transcriptId, organizationId)` directly. Callers holding only `recordings.read.own` must own the transcript\'s appointment or form submission. 
         * @summary Get transcript by ID
         * @param {string} transcriptId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTranscript: async (transcriptId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'transcriptId' is not null or undefined
            assertParamExists('getTranscript', 'transcriptId', transcriptId)
            const localVarPath = `/api/v1/transcripts/{transcriptId}`
                .replace(`{${"transcriptId"}}`, encodeURIComponent(String(transcriptId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the real-time streaming transcription segments as a fallback when batch transcription fails, for a transcript loaded by `(transcriptId, organizationId)` directly. Callers holding only `recordings.read.own` must own the transcript\'s appointment or form submission.  Returns 404 when no streaming segments are available. 
         * @summary Get streaming segments fallback by transcript ID
         * @param {string} transcriptId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTranscriptStreamingSegments: async (transcriptId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'transcriptId' is not null or undefined
            assertParamExists('getTranscriptStreamingSegments', 'transcriptId', transcriptId)
            const localVarPath = `/api/v1/transcripts/{transcriptId}/streaming-segments`
                .replace(`{${"transcriptId"}}`, encodeURIComponent(String(transcriptId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Lists transcription sessions filtered by their owner. At least one of `appointmentId` or `formSubmissionId` must be supplied; a request with neither filter is rejected with 400. When both are supplied, `appointmentId` takes precedence (a session is owned by exactly one entity).  Callers holding only `recordings.read.own` must own the referenced appointment (behandelaar/attendee) or form submission (creator). Defaults to ACTIVE sessions when `status` is omitted. 
         * @summary List transcription sessions by owner
         * @param {string} [appointmentId] Filter sessions owned by this appointment
         * @param {string} [formSubmissionId] Filter sessions owned by this form submission
         * @param {SessionStatus} [status] Filter by session status. Defaults to ACTIVE.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listTranscriptionSessionsByFilter: async (appointmentId?: string, formSubmissionId?: string, status?: SessionStatus, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/transcription-sessions`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (appointmentId !== undefined) {
                localVarQueryParameter['appointmentId'] = appointmentId;
            }

            if (formSubmissionId !== undefined) {
                localVarQueryParameter['formSubmissionId'] = formSubmissionId;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Lists transcripts filtered by their owner. At least one of `appointmentId` or `formSubmissionId` must be supplied; a request with neither filter is rejected with 400.  Transcripts are loaded by `(filter, organizationId)` directly — there is no appointment-coupled URL. Callers holding only `recordings.read.own` must own the referenced appointment (behandelaar/attendee) or form submission (creator).  Results are ordered by createdAt descending (most recent first). 
         * @summary List transcripts by owner
         * @param {string} [appointmentId] Filter transcripts owned by this appointment
         * @param {string} [formSubmissionId] Filter transcripts owned by this form submission
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listTranscriptsByFilter: async (appointmentId?: string, formSubmissionId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/transcripts`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (appointmentId !== undefined) {
                localVarQueryParameter['appointmentId'] = appointmentId;
            }

            if (formSubmissionId !== undefined) {
                localVarQueryParameter['formSubmissionId'] = formSubmissionId;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Triggers a new batch transcription job for a failed transcript, loaded by `(transcriptId, organizationId)` directly. Only works when the current transcript status is FAILED. 
         * @summary Retry batch transcription by transcript ID
         * @param {string} transcriptId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        retryTranscriptById: async (transcriptId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'transcriptId' is not null or undefined
            assertParamExists('retryTranscriptById', 'transcriptId', transcriptId)
            const localVarPath = `/api/v1/transcripts/{transcriptId}/retry`
                .replace(`{${"transcriptId"}}`, encodeURIComponent(String(transcriptId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * TranscriptionsApi - functional programming interface
 */
export const TranscriptionsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = TranscriptionsApiAxiosParamCreator(configuration)
    return {
        /**
         * Starts a transcription session owned by exactly one of an appointment or a form submission. The request body must supply exactly one of `appointmentId` or `formSubmissionId`; a request with neither or both is rejected with 400.  When `formSubmissionId` is supplied and that submission is linked to an appointment, the session is owned by the appointment instead (matching the form-submission ownership rules).  An ACTIVE session the owner still carries is superseded — marked FAILED — rather than rejected. A session only leaves ACTIVE on a clean stop, so a recording killed mid-stream would otherwise make its appointment permanently unrecordable (#3350). 
         * @summary Start a transcription session
         * @param {CreateTranscriptionSessionRequest} createTranscriptionSessionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createTranscriptionSession(createTranscriptionSessionRequest: CreateTranscriptionSessionRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TranscriptionSessionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createTranscriptionSession(createTranscriptionSessionRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TranscriptionsApi.createTranscriptionSession']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Soft-deletes a specific transcript, loaded by `(transcriptId, organizationId)` directly. Requires transcripts.delete or recordings.delete permission.  Refused with `409` while the transcript\'s transcription session is still `ACTIVE`. A delete only sets `deleted_at` and says nothing to the whisper service, so deleting a transcript that is still being recorded into leaves that recording finalizing into a soft-deleted row. 
         * @summary Delete transcript by ID
         * @param {string} transcriptId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteTranscriptById(transcriptId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteTranscriptById(transcriptId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TranscriptionsApi.deleteTranscriptById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns one AI-generated summary covering **all** transcripts of the appointment, grouped together.  The summary is precomputed and stored server-side whenever the appointment\'s transcripts change, so this normally reads a stored row rather than calling the model. On a miss (an appointment transcribed before the summary was introduced, or a failed warm-up) it is generated and stored on the spot.  Returns 204 No Content when the appointment has no completed transcript text, or when the model judged the content too thin for a meaningful summary.  Callers holding only `recordings.read.own` must own the appointment (behandelaar/attendee). 
         * @summary Get the AI summary of an appointment\'s transcripts
         * @param {string} appointmentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAppointmentTranscriptSummary(appointmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentTranscriptSummaryResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAppointmentTranscriptSummary(appointmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TranscriptionsApi.getAppointmentTranscriptSummary']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a specific transcript with its segments, loaded by `(transcriptId, organizationId)` directly. Callers holding only `recordings.read.own` must own the transcript\'s appointment or form submission. 
         * @summary Get transcript by ID
         * @param {string} transcriptId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTranscript(transcriptId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TranscriptResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTranscript(transcriptId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TranscriptionsApi.getTranscript']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the real-time streaming transcription segments as a fallback when batch transcription fails, for a transcript loaded by `(transcriptId, organizationId)` directly. Callers holding only `recordings.read.own` must own the transcript\'s appointment or form submission.  Returns 404 when no streaming segments are available. 
         * @summary Get streaming segments fallback by transcript ID
         * @param {string} transcriptId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getTranscriptStreamingSegments(transcriptId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<StreamingSegmentsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getTranscriptStreamingSegments(transcriptId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TranscriptionsApi.getTranscriptStreamingSegments']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Lists transcription sessions filtered by their owner. At least one of `appointmentId` or `formSubmissionId` must be supplied; a request with neither filter is rejected with 400. When both are supplied, `appointmentId` takes precedence (a session is owned by exactly one entity).  Callers holding only `recordings.read.own` must own the referenced appointment (behandelaar/attendee) or form submission (creator). Defaults to ACTIVE sessions when `status` is omitted. 
         * @summary List transcription sessions by owner
         * @param {string} [appointmentId] Filter sessions owned by this appointment
         * @param {string} [formSubmissionId] Filter sessions owned by this form submission
         * @param {SessionStatus} [status] Filter by session status. Defaults to ACTIVE.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listTranscriptionSessionsByFilter(appointmentId?: string, formSubmissionId?: string, status?: SessionStatus, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TranscriptionSessionListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listTranscriptionSessionsByFilter(appointmentId, formSubmissionId, status, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TranscriptionsApi.listTranscriptionSessionsByFilter']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Lists transcripts filtered by their owner. At least one of `appointmentId` or `formSubmissionId` must be supplied; a request with neither filter is rejected with 400.  Transcripts are loaded by `(filter, organizationId)` directly — there is no appointment-coupled URL. Callers holding only `recordings.read.own` must own the referenced appointment (behandelaar/attendee) or form submission (creator).  Results are ordered by createdAt descending (most recent first). 
         * @summary List transcripts by owner
         * @param {string} [appointmentId] Filter transcripts owned by this appointment
         * @param {string} [formSubmissionId] Filter transcripts owned by this form submission
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listTranscriptsByFilter(appointmentId?: string, formSubmissionId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TranscriptListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listTranscriptsByFilter(appointmentId, formSubmissionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TranscriptionsApi.listTranscriptsByFilter']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Triggers a new batch transcription job for a failed transcript, loaded by `(transcriptId, organizationId)` directly. Only works when the current transcript status is FAILED. 
         * @summary Retry batch transcription by transcript ID
         * @param {string} transcriptId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async retryTranscriptById(transcriptId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TranscriptResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.retryTranscriptById(transcriptId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['TranscriptionsApi.retryTranscriptById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * TranscriptionsApi - factory interface
 */
export const TranscriptionsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = TranscriptionsApiFp(configuration)
    return {
        /**
         * Starts a transcription session owned by exactly one of an appointment or a form submission. The request body must supply exactly one of `appointmentId` or `formSubmissionId`; a request with neither or both is rejected with 400.  When `formSubmissionId` is supplied and that submission is linked to an appointment, the session is owned by the appointment instead (matching the form-submission ownership rules).  An ACTIVE session the owner still carries is superseded — marked FAILED — rather than rejected. A session only leaves ACTIVE on a clean stop, so a recording killed mid-stream would otherwise make its appointment permanently unrecordable (#3350). 
         * @summary Start a transcription session
         * @param {CreateTranscriptionSessionRequest} createTranscriptionSessionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createTranscriptionSession(createTranscriptionSessionRequest: CreateTranscriptionSessionRequest, options?: RawAxiosRequestConfig): AxiosPromise<TranscriptionSessionResponse> {
            return localVarFp.createTranscriptionSession(createTranscriptionSessionRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Soft-deletes a specific transcript, loaded by `(transcriptId, organizationId)` directly. Requires transcripts.delete or recordings.delete permission.  Refused with `409` while the transcript\'s transcription session is still `ACTIVE`. A delete only sets `deleted_at` and says nothing to the whisper service, so deleting a transcript that is still being recorded into leaves that recording finalizing into a soft-deleted row. 
         * @summary Delete transcript by ID
         * @param {string} transcriptId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteTranscriptById(transcriptId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteTranscriptById(transcriptId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns one AI-generated summary covering **all** transcripts of the appointment, grouped together.  The summary is precomputed and stored server-side whenever the appointment\'s transcripts change, so this normally reads a stored row rather than calling the model. On a miss (an appointment transcribed before the summary was introduced, or a failed warm-up) it is generated and stored on the spot.  Returns 204 No Content when the appointment has no completed transcript text, or when the model judged the content too thin for a meaningful summary.  Callers holding only `recordings.read.own` must own the appointment (behandelaar/attendee). 
         * @summary Get the AI summary of an appointment\'s transcripts
         * @param {string} appointmentId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentTranscriptSummary(appointmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentTranscriptSummaryResponse> {
            return localVarFp.getAppointmentTranscriptSummary(appointmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a specific transcript with its segments, loaded by `(transcriptId, organizationId)` directly. Callers holding only `recordings.read.own` must own the transcript\'s appointment or form submission. 
         * @summary Get transcript by ID
         * @param {string} transcriptId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTranscript(transcriptId: string, options?: RawAxiosRequestConfig): AxiosPromise<TranscriptResponse> {
            return localVarFp.getTranscript(transcriptId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the real-time streaming transcription segments as a fallback when batch transcription fails, for a transcript loaded by `(transcriptId, organizationId)` directly. Callers holding only `recordings.read.own` must own the transcript\'s appointment or form submission.  Returns 404 when no streaming segments are available. 
         * @summary Get streaming segments fallback by transcript ID
         * @param {string} transcriptId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getTranscriptStreamingSegments(transcriptId: string, options?: RawAxiosRequestConfig): AxiosPromise<StreamingSegmentsResponse> {
            return localVarFp.getTranscriptStreamingSegments(transcriptId, options).then((request) => request(axios, basePath));
        },
        /**
         * Lists transcription sessions filtered by their owner. At least one of `appointmentId` or `formSubmissionId` must be supplied; a request with neither filter is rejected with 400. When both are supplied, `appointmentId` takes precedence (a session is owned by exactly one entity).  Callers holding only `recordings.read.own` must own the referenced appointment (behandelaar/attendee) or form submission (creator). Defaults to ACTIVE sessions when `status` is omitted. 
         * @summary List transcription sessions by owner
         * @param {string} [appointmentId] Filter sessions owned by this appointment
         * @param {string} [formSubmissionId] Filter sessions owned by this form submission
         * @param {SessionStatus} [status] Filter by session status. Defaults to ACTIVE.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listTranscriptionSessionsByFilter(appointmentId?: string, formSubmissionId?: string, status?: SessionStatus, options?: RawAxiosRequestConfig): AxiosPromise<TranscriptionSessionListResponse> {
            return localVarFp.listTranscriptionSessionsByFilter(appointmentId, formSubmissionId, status, options).then((request) => request(axios, basePath));
        },
        /**
         * Lists transcripts filtered by their owner. At least one of `appointmentId` or `formSubmissionId` must be supplied; a request with neither filter is rejected with 400.  Transcripts are loaded by `(filter, organizationId)` directly — there is no appointment-coupled URL. Callers holding only `recordings.read.own` must own the referenced appointment (behandelaar/attendee) or form submission (creator).  Results are ordered by createdAt descending (most recent first). 
         * @summary List transcripts by owner
         * @param {string} [appointmentId] Filter transcripts owned by this appointment
         * @param {string} [formSubmissionId] Filter transcripts owned by this form submission
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listTranscriptsByFilter(appointmentId?: string, formSubmissionId?: string, options?: RawAxiosRequestConfig): AxiosPromise<TranscriptListResponse> {
            return localVarFp.listTranscriptsByFilter(appointmentId, formSubmissionId, options).then((request) => request(axios, basePath));
        },
        /**
         * Triggers a new batch transcription job for a failed transcript, loaded by `(transcriptId, organizationId)` directly. Only works when the current transcript status is FAILED. 
         * @summary Retry batch transcription by transcript ID
         * @param {string} transcriptId 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        retryTranscriptById(transcriptId: string, options?: RawAxiosRequestConfig): AxiosPromise<TranscriptResponse> {
            return localVarFp.retryTranscriptById(transcriptId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * TranscriptionsApi - object-oriented interface
 */
export class TranscriptionsApi extends BaseAPI {
    /**
     * Starts a transcription session owned by exactly one of an appointment or a form submission. The request body must supply exactly one of `appointmentId` or `formSubmissionId`; a request with neither or both is rejected with 400.  When `formSubmissionId` is supplied and that submission is linked to an appointment, the session is owned by the appointment instead (matching the form-submission ownership rules).  An ACTIVE session the owner still carries is superseded — marked FAILED — rather than rejected. A session only leaves ACTIVE on a clean stop, so a recording killed mid-stream would otherwise make its appointment permanently unrecordable (#3350). 
     * @summary Start a transcription session
     * @param {CreateTranscriptionSessionRequest} createTranscriptionSessionRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createTranscriptionSession(createTranscriptionSessionRequest: CreateTranscriptionSessionRequest, options?: RawAxiosRequestConfig) {
        return TranscriptionsApiFp(this.configuration).createTranscriptionSession(createTranscriptionSessionRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Soft-deletes a specific transcript, loaded by `(transcriptId, organizationId)` directly. Requires transcripts.delete or recordings.delete permission.  Refused with `409` while the transcript\'s transcription session is still `ACTIVE`. A delete only sets `deleted_at` and says nothing to the whisper service, so deleting a transcript that is still being recorded into leaves that recording finalizing into a soft-deleted row. 
     * @summary Delete transcript by ID
     * @param {string} transcriptId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteTranscriptById(transcriptId: string, options?: RawAxiosRequestConfig) {
        return TranscriptionsApiFp(this.configuration).deleteTranscriptById(transcriptId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns one AI-generated summary covering **all** transcripts of the appointment, grouped together.  The summary is precomputed and stored server-side whenever the appointment\'s transcripts change, so this normally reads a stored row rather than calling the model. On a miss (an appointment transcribed before the summary was introduced, or a failed warm-up) it is generated and stored on the spot.  Returns 204 No Content when the appointment has no completed transcript text, or when the model judged the content too thin for a meaningful summary.  Callers holding only `recordings.read.own` must own the appointment (behandelaar/attendee). 
     * @summary Get the AI summary of an appointment\'s transcripts
     * @param {string} appointmentId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAppointmentTranscriptSummary(appointmentId: string, options?: RawAxiosRequestConfig) {
        return TranscriptionsApiFp(this.configuration).getAppointmentTranscriptSummary(appointmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a specific transcript with its segments, loaded by `(transcriptId, organizationId)` directly. Callers holding only `recordings.read.own` must own the transcript\'s appointment or form submission. 
     * @summary Get transcript by ID
     * @param {string} transcriptId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getTranscript(transcriptId: string, options?: RawAxiosRequestConfig) {
        return TranscriptionsApiFp(this.configuration).getTranscript(transcriptId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the real-time streaming transcription segments as a fallback when batch transcription fails, for a transcript loaded by `(transcriptId, organizationId)` directly. Callers holding only `recordings.read.own` must own the transcript\'s appointment or form submission.  Returns 404 when no streaming segments are available. 
     * @summary Get streaming segments fallback by transcript ID
     * @param {string} transcriptId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getTranscriptStreamingSegments(transcriptId: string, options?: RawAxiosRequestConfig) {
        return TranscriptionsApiFp(this.configuration).getTranscriptStreamingSegments(transcriptId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Lists transcription sessions filtered by their owner. At least one of `appointmentId` or `formSubmissionId` must be supplied; a request with neither filter is rejected with 400. When both are supplied, `appointmentId` takes precedence (a session is owned by exactly one entity).  Callers holding only `recordings.read.own` must own the referenced appointment (behandelaar/attendee) or form submission (creator). Defaults to ACTIVE sessions when `status` is omitted. 
     * @summary List transcription sessions by owner
     * @param {string} [appointmentId] Filter sessions owned by this appointment
     * @param {string} [formSubmissionId] Filter sessions owned by this form submission
     * @param {SessionStatus} [status] Filter by session status. Defaults to ACTIVE.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listTranscriptionSessionsByFilter(appointmentId?: string, formSubmissionId?: string, status?: SessionStatus, options?: RawAxiosRequestConfig) {
        return TranscriptionsApiFp(this.configuration).listTranscriptionSessionsByFilter(appointmentId, formSubmissionId, status, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Lists transcripts filtered by their owner. At least one of `appointmentId` or `formSubmissionId` must be supplied; a request with neither filter is rejected with 400.  Transcripts are loaded by `(filter, organizationId)` directly — there is no appointment-coupled URL. Callers holding only `recordings.read.own` must own the referenced appointment (behandelaar/attendee) or form submission (creator).  Results are ordered by createdAt descending (most recent first). 
     * @summary List transcripts by owner
     * @param {string} [appointmentId] Filter transcripts owned by this appointment
     * @param {string} [formSubmissionId] Filter transcripts owned by this form submission
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listTranscriptsByFilter(appointmentId?: string, formSubmissionId?: string, options?: RawAxiosRequestConfig) {
        return TranscriptionsApiFp(this.configuration).listTranscriptsByFilter(appointmentId, formSubmissionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Triggers a new batch transcription job for a failed transcript, loaded by `(transcriptId, organizationId)` directly. Only works when the current transcript status is FAILED. 
     * @summary Retry batch transcription by transcript ID
     * @param {string} transcriptId 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public retryTranscriptById(transcriptId: string, options?: RawAxiosRequestConfig) {
        return TranscriptionsApiFp(this.configuration).retryTranscriptById(transcriptId, options).then((request) => request(this.axios, this.basePath));
    }
}



